/**
 * One screen of the profile, opened from its row.
 *
 * The whole screen on a phone, as Edit plant and the photo screens are, with
 * ‹ to go back to the list. A screen whose fields are typed says so by being
 * given `dirty` and `onSave`: leaving it with changes then asks "Save or
 * Discard" rather than losing them or keeping them without being asked.
 *
 * Used by: every profile screen in this folder (ten of them).
 *
 * @module ProfileScreen
 */

import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { cn } from '@/lib/utils';

/** The screen's title and contents, and how it handles unsaved changes. */
interface ProfileScreenProps {
  /** Whether the screen is showing (UserProfile: its name is the address's ?s=). */
  open: boolean;
  /** The heading beside ‹, also the dialog's accessible name. */
  title: string;
  /** Back to the list; called once leaving is settled (saved, discarded, or nothing to ask). */
  onClose: () => void;
  /** The screen's body, which scrolls between the heading and the footer. */
  children: React.ReactNode;
  /** Pinned at the foot, for a screen with a Save. */
  footer?: React.ReactNode;
  /** Whether typed changes are waiting to be saved. */
  dirty?: boolean;
  /** Saves them; resolves true when they were kept. */
  onSave?: () => Promise<boolean>;
  /** Forgets them. */
  onDiscard?: () => void;
}

/** One full-screen page of the profile, which asks before typed changes are lost. */
export const ProfileScreen: React.FC<ProfileScreenProps> = ({
  open, title, onClose, children, footer, dirty = false, onSave, onDiscard,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [asking, setAsking] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { if (!open) setAsking(false); }, [open]);

  // A tab closed with typed changes still gets the browser's own question.
  React.useEffect(() => {
    if (!open || !dirty) return undefined;
    /** Asks the browser to confirm leaving. */
    const warn = (event: BeforeUnloadEvent): void => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [open, dirty]);

  /** Back to the list, asking first when typed changes are waiting. */
  const leave = (): void => {
    if (dirty) { setAsking(true); return; }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) leave(); }}>
      <DialogContent
        scrollBody={false}
        aria-describedby={undefined}
        // A picker or panel opened from inside this screen (the photo picker) lives in its own dialog; closing it must not
        // read as leaving this screen. Interactions inside another dialog, or with focus dropped on the page as that dialog
        // unmounts, are not "outside" this one.
        onInteractOutside={(event) => {
          const target = event.target as Element | null;
          if (!target || target === document.body || target.closest?.('[role="dialog"]')) event.preventDefault();
        }}
        className="flex h-[100dvh] max-h-none flex-col gap-0 overflow-hidden rounded-none border-0 bg-background p-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 sm:h-[min(90dvh,760px)] sm:max-w-lg sm:rounded-lg sm:border"
      >
        <DialogHeader className="flex min-h-[3.5rem] shrink-0 flex-row items-center gap-1 space-y-0 border-b bg-card py-2 pl-2 pr-14 text-left">
          <button
            type="button"
            onClick={leave}
            aria-label={t('profile.back')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <DialogTitle className="truncate">{title}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">{children}</div>

        {footer && (
          <div className={cn('flex shrink-0 items-center gap-3 border-t bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]')}>
            {footer}
          </div>
        )}
      </DialogContent>

      <BottomPanel open={asking} onOpenChange={(next) => { if (!next && !saving) setAsking(false); }} title={t('profile.leave.title')}>
        <p className="text-sm text-muted-foreground">{t('profile.leave.body')}</p>
        <ActionRow
          cancelLabel={t('profile.leave.discard')}
          disabled={saving}
          onCancel={() => { onDiscard?.(); setAsking(false); onClose(); }}
        >
          <Button
            disabled={!onSave}
            loading={saving}
            onClick={async () => {
              if (!onSave) return;
              setSaving(true);
              // A save that throws keeps the question open, as one that says no
              // does not close the screen; the buttons must not stay greyed.
              let kept = false;
              try {
                kept = await onSave();
              } catch {
                setSaving(false);
                return;
              }
              setSaving(false);
              setAsking(false);
              if (kept) onClose();
            }}
          >
            {t('sitewide.save')}
          </Button>
        </ActionRow>
      </BottomPanel>
    </Dialog>
  );
};
